import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { InfoBanner } from "@/components/ui/banner";
import { Textarea } from "@/components/ui/form-controls";
import { toast } from "@/components/ui/toast";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { usePlainErrorMessage } from "@/components/data-display/api-failure";

/** ALL-5774: decision refusals that need their own words, not a generic category sentence. */
const DENIAL_SENTENCES: Record<string, string> = {
  CREATOR_SELF_APPROVAL_DENIED: "You created this request, so you can't approve it. Another approver has to review it.",
};
import { cn } from "@/lib/utils";
import { approveApproval, rejectApproval, withStepUpRetry, idempotencyKey } from "@/lib/api";
import { usePermission } from "@/hooks/use-permission";
import { useTransferDetail } from "@/hooks/use-transfer-detail";
import { transferStatusLabel } from "@/features/transfers/transfer-state";
import { useMobileSigningLocale } from "@/i18n/use-mobile-signing-locale";
import type { ApprovalDetail, ApprovalStatus } from "@/types";

/* Mirrors transfer-detail.tsx's WalletRefText — only "Name (WLT-NNN)" style
   internal wallet references get a real link; external addresses/raw ids
   stay plain text. */
function WalletRefText({ text }: { text: string }) {
  const match = text.match(/\(WLT-\d+\)/);
  if (!match) return <>{text}</>;
  const walletId = match[0].slice(1, -1);
  return (
    <Link to={`/wallets/${walletId}`} className="font-semibold text-primary hover:underline">
      {text}
    </Link>
  );
}

const DIALOG_CARD =
  "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-[16px] bg-surface p-8 shadow-[0_24px_48px_rgba(16,24,32,0.18)] outline-none";

function DialogShell({ onClose, label, children }: {
  onClose?: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <DialogPrimitive.Root defaultOpen onOpenChange={(open) => !open && onClose?.()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <DialogPrimitive.Content className={DIALOG_CARD}>
          <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {label} — review and confirm, or cancel.
          </DialogPrimitive.Description>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function ConfirmApprovalDialog({ detail, submitting, onConfirm, onClose }: {
  detail: ApprovalDetail;
  submitting: boolean;
  onConfirm: () => void;
  onClose?: () => void;
}) {
  const q = detail.quorumLabel.match(/Required:\s*(\d+)\s*of\s*(\d+)/);
  const required = q ? `${q[1]} of ${q[2]} required` : "";
  return (
    <DialogShell onClose={onClose} label={`Approve Transfer ${detail.transferId}?`}>
      <div className="flex flex-col items-center text-center">
        <span aria-hidden className="text-[32px] leading-none">✅</span>
        <h2 className="mt-3 text-[19px] font-bold text-ink">
          Approve Transfer {detail.transferId}?
        </h2>
        {required && (
          <p className="mt-2 max-w-[396px] text-[12.5px] leading-relaxed text-muted">
            You are recording an approval ({required}). This calls the real approval decision endpoint.
          </p>
        )}
        <div className="mt-5 w-full rounded-lg bg-bg px-4 py-3">
          {[
            { k: "Amount", v: detail.amount, present: Boolean(detail.amount), strong: true },
            { k: "From", v: detail.source, present: Boolean(detail.source) },
            { k: "To", v: detail.destination, present: Boolean(detail.destination) },
          ]
            .filter((row) => row.present)
            .map((row) => (
            <div key={row.k} className="flex items-center justify-between gap-4 border-b border-line/60 py-1 last:border-b-0">
              <span className="text-[12px] text-muted">{row.k}</span>
              <span className={cn("text-right text-[12.5px] text-ink", row.strong ? "font-bold" : "font-medium")}>
                {row.v}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-5 flex w-full items-center justify-center gap-2">
          <DialogPrimitive.Close className="inline-flex" asChild>
            <Button variant="ghost" disabled={submitting}>Cancel</Button>
          </DialogPrimitive.Close>
          <Button variant="primary" onClick={onConfirm} disabled={submitting}>
            {submitting ? "Approving…" : "Confirm Approval"}
          </Button>
        </div>
        <p className="mt-5 text-[10.5px] leading-relaxed text-subtle">
          This calls POST /v1/approvals/{"{id}"}/approve on the real backend. Four-eyes separation is enforced
          server-side — the transfer's initiator cannot approve it.
        </p>
      </div>
    </DialogShell>
  );
}

function RejectDialog({ detail, submitting, onConfirm, onClose }: {
  detail: ApprovalDetail;
  submitting: boolean;
  onConfirm: (comment: string) => void;
  onClose?: () => void;
}) {
  const [comment, setComment] = React.useState("");
  return (
    <DialogShell onClose={onClose} label={`Reject Transfer ${detail.transferId}?`}>
      <div className="flex flex-col">
        <h2 className="text-[17px] font-bold text-ink">Reject Transfer {detail.transferId}?</h2>
        <p className="mt-1 text-[12px] text-muted">
          Calls POST /v1/approvals/{"{id}"}/reject. A reason is recorded with the decision.
        </p>
        <Textarea
          className="mt-4"
          rows={3}
          aria-label="Reason for rejection"
          placeholder="Reason for rejection"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={submitting}
        />
        <div className="mt-4 flex items-center justify-end gap-2">
          <DialogPrimitive.Close className="inline-flex" asChild>
            <Button variant="ghost" disabled={submitting}>Cancel</Button>
          </DialogPrimitive.Close>
          <Button variant="destructive" onClick={() => onConfirm(comment)} disabled={submitting || !comment.trim()}>
            {submitting ? "Rejecting…" : "Confirm Reject"}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}

/**
 * Once this approval is decided, the underlying transfer may already be in a
 * mobile-signing state (ALL-4884): approvals recorded, now waiting on the
 * paired phone, or already broadcast/confirmed with a real tx hash. Fetches
 * the real transfer detail (the same source-of-truth as /transfers/{id})
 * rather than inventing a status here; renders nothing while loading or once
 * the transfer has moved past these three states (its own page covers those).
 */
function ApprovedTransferState({ transferId }: { transferId: string }) {
  const { detail, loading } = useTransferDetail(transferId);
  const catalog = useMobileSigningLocale();
  const t = catalog.transferState;
  if (loading || !detail) return null;
  const state = detail.defaultState;
  if (state !== "pending-device-signature" && state !== "broadcast" && state !== "confirmed") return null;
  return (
    <div
      dir={catalog.dir}
      className="mt-3 rounded-lg border border-line-soft bg-surface px-3.5 py-2.5 text-[12px]"
      data-testid="approval-transfer-state"
    >
      <div className="font-semibold text-ink">
        {state === "pending-device-signature" ? t.pendingDeviceSignature : transferStatusLabel(state)}
      </div>
      {state === "pending-device-signature" && (
        <p className="mt-0.5 text-muted">{t.pendingDeviceSignatureDetail}</p>
      )}
      {detail.finality?.txHash && (
        <a
          href={`https://sepolia.etherscan.io/tx/${detail.finality.txHash}`}
          target="_blank"
          rel="noreferrer"
          dir="ltr"
          className="mt-1 inline-block font-mono font-semibold text-primary underline"
        >
          {detail.finality.txHash} · Etherscan ↗
        </a>
      )}
    </div>
  );
}

type DialogStep = "idle" | "confirm-approve" | "confirm-reject";

/**
 * The step-up round trip itself failed (AUTH_ASSURANCE_INSUFFICIENT/
 * AUTH_STEP_UP_EXPIRED from completeStepUpChallenge) rather than the
 * original approve/reject call failing for an ordinary reason. There is no
 * in-page recovery yet (ALL-5021's redirect-resume login intent lands
 * separately) -- the plain, honest instruction is to sign in again with
 * MFA, never a fabricated retry or a decorative "enter your passkey" input.
 */
function stepUpFailedDenial(outcome: { stepUpReasonCode?: string; stepUpSupportReference?: string }) {
  return {
    error: "This action needs a recent multi-factor sign-in. Sign out, sign in again with MFA, then retry.",
    reasonCode: outcome.stepUpReasonCode,
    supportReference: outcome.stepUpSupportReference,
  };
}

export default function ApprovalDetailFlow({ detail, status, receiptRef, onDecided }: {
  detail: ApprovalDetail;
  status: ApprovalStatus;
  receiptRef: string | null;
  onDecided: () => void;
}) {
  const navigate = useNavigate();
  const canApprove = usePermission("approval:approve");
  const canReject = usePermission("approval:reject");
  const [step, setStep] = React.useState<DialogStep>("idle");
  const [submitting, setSubmitting] = React.useState(false);
  const [denial, setDenial] = React.useState<{ error: string; reasonCode?: string; supportReference?: string; capability?: string } | null>(null);
  const plainError = usePlainErrorMessage();
  const isResolved = status !== "pending";
  const riskVariant = detail.risk === "high" ? "warning" : "success";
  const riskLabel = detail.risk === "high" ? "High Value" : "Low Risk";

  const runApprove = async () => {
    setSubmitting(true);
    setDenial(null);
    // Same key on the first try and the step-up retry (ALL-5022): the gateway must see one
    // decision, not two, if a step-up round trip happens in between.
    const key = idempotencyKey("approval_approve");
    const outcome = await withStepUpRetry(() => approveApproval(detail.id, undefined, key));
    setSubmitting(false);
    setStep("idle");
    if (outcome.result.ok) {
      toast({
        variant: "success",
        title: `${detail.approvalId} decision recorded`,
        description: `${detail.transferId} · ${detail.amount} approval was submitted to the real backend.`,
        supportReference: outcome.result.supportReference,
      });
      onDecided();
    } else if (outcome.stepUpFailed) {
      setDenial(stepUpFailedDenial(outcome));
    } else {
      const result = outcome.result;
      setDenial({ error: result.error ?? "Approve failed", reasonCode: result.reasonCode, supportReference: result.supportReference, capability: result.capability });
    }
  };

  const runReject = async (comment: string) => {
    setSubmitting(true);
    setDenial(null);
    const key = idempotencyKey("approval_reject");
    const outcome = await withStepUpRetry(() => rejectApproval(detail.id, comment, key));
    setSubmitting(false);
    setStep("idle");
    if (outcome.result.ok) {
      toast({
        variant: "success",
        title: `${detail.approvalId} rejected`,
        description: `${detail.transferId} rejection was submitted to the real backend.`,
        supportReference: outcome.result.supportReference,
      });
      onDecided();
    } else if (outcome.stepUpFailed) {
      setDenial(stepUpFailedDenial(outcome));
    } else {
      const result = outcome.result;
      setDenial({ error: result.error ?? "Reject failed", reasonCode: result.reasonCode, supportReference: result.supportReference, capability: result.capability });
    }
  };

  return (
    <>
      <Breadcrumb items={[{ label: "Approvals", href: "/approvals" }, { label: detail.approvalId }]} />
      <div className="max-w-[720px]">
        <h1 className="text-xl font-bold text-ink">
          {detail.approvalId} — Transfer {detail.transferId}
        </h1>
        {detail.quorumLabel && <p className="mt-1 text-[12.5px] text-muted">{detail.quorumLabel}</p>}

        {status === "approved" && (
          <InfoBanner variant="success" title="Approval recorded" className="mt-4">
            {detail.approvalId} was approved{receiptRef ? ` — receipt ${receiptRef}` : ""}.
            <ApprovedTransferState transferId={detail.transferId} />
          </InfoBanner>
        )}
        {status === "rejected" && (
          <InfoBanner variant="danger" title="Approval rejected" className="mt-4">
            {detail.approvalId} was rejected. This transfer is terminal — see the transfer detail for the recorded reason.
          </InfoBanner>
        )}
        {status === "escalated" && (
          <InfoBanner variant="warning" title="Escalated" className="mt-4">
            {detail.approvalId} was escalated. No further action is available here.
          </InfoBanner>
        )}
        {denial && (
          <InfoBanner variant="danger" title="Denied" className="mt-4">
            {/* ALL-5774: a sentence, never the raw reason code as the message. A server
                message that is itself just a code falls back to the category message; the
                code stays below as a secondary detail for support. */}
            {DENIAL_SENTENCES[denial.reasonCode ?? ""]
              ?? (denial.error && denial.error !== denial.reasonCode && !/^[A-Z][A-Z0-9_]+$/.test(denial.error)
                ? denial.error
                : plainError({ reasonCode: denial.reasonCode ?? denial.error }))}
            {denial.reasonCode && <span className="mt-1 block font-mono text-xs">Code: {denial.reasonCode}</span>}
            <SupportDetails reference={denial.supportReference} capability={denial.capability} />
          </InfoBanner>
        )}

        <div className="mt-4 rounded-[10px] border border-line bg-surface px-5 py-2">
          {[
            { k: "Approval ID", v: detail.approvalId, present: Boolean(detail.approvalId), strong: true },
            {
              k: "Transfer",
              v: (
                <Link to={`/transfers/${detail.transferId}`} className="font-semibold text-primary hover:underline">
                  {detail.transferId}
                </Link>
              ),
              present: Boolean(detail.transferId),
              strong: false,
            },
            { k: "Amount", v: detail.amount, present: Boolean(detail.amount), strong: true, big: true },
            { k: "Source", v: <WalletRefText text={detail.source} />, present: Boolean(detail.source), strong: false },
            { k: "Destination", v: <WalletRefText text={detail.destination} />, present: Boolean(detail.destination), strong: false },
            { k: "Requester", v: detail.requester, present: Boolean(detail.requester), strong: true },
          ]
            // A row is dropped when the backend returned no value for it. The
            // Source/Destination cells wrap their text in an element, so
            // `Boolean(row.v)` is always true for those and let an empty value
            // through as a label next to a blank cell.
            .filter((row) => row.present)
            .map((row) => (
              <div key={row.k} className="flex items-baseline justify-between gap-4 py-2">
                <span className="w-[120px] shrink-0 text-[12px] text-muted">{row.k}</span>
                <span
                  className={cn(
                    "text-right",
                    row.strong && "font-medium text-ink",
                    !row.strong && "text-ink",
                    row.big && "text-[15px] font-bold",
                    !row.big && "text-[12.5px]"
                  )}
                >
                  {row.v}
                </span>
              </div>
            ))}
          <div className="flex items-baseline justify-between gap-4 py-2">
            <span className="w-[120px] shrink-0 text-[12px] text-muted">Risk Level</span>
            <Badge variant={riskVariant} dot={false} size="sm">
              {riskLabel}
            </Badge>
          </div>
        </div>

        {detail.policyTitle && (
          <>
            {/* Design-port: the prototype's section heading is "Policy Triggers".
                Kept deliberately neutral (line/surface, no success tone or
                "✓ N thresholds matched" stamp) because the gateway returns only
                the matched policy *name* — it carries no match verdict or
                threshold count, and inventing one would be fabricated UI. */}
            <h2 className="mt-6 text-[15px] font-bold text-ink">Policy Triggers</h2>
            <div className="mt-2 rounded-lg border border-line bg-surface px-4 py-3">
              <div className="text-[11px] font-medium text-muted">Policy Trigger</div>
              <div className="mt-0.5 text-[13px] font-bold text-primary">
                {(() => {
                  const m = detail.policyTitle.match(/^(POL-\d+)/);
                  return m ? (
                    <Link to={`/governance/${m[1]}`} className="hover:underline">
                      {detail.policyTitle}
                    </Link>
                  ) : (
                    detail.policyTitle
                  );
                })()}
              </div>
            </div>
          </>
        )}

        {detail.quorumLabel && (
          <>
            <h2 className="mt-4 text-[15px] font-bold text-ink">Quorum</h2>
            <p className="mt-1 text-xs text-muted">{detail.quorumLabel}</p>
            <div className="mt-2 flex h-2 w-[300px] max-w-full overflow-hidden rounded-[4px] bg-disabled-soft">
              <div aria-hidden className="h-full bg-warning" style={{ width: `${detail.quorumPct}%` }} />
            </div>
          </>
        )}

        {isResolved ? (
          <div className="mt-6">
            <Button variant="primary" onClick={() => navigate("/approvals")}>
              Back to Approvals
            </Button>
          </div>
        ) : (
          <div className="mt-6 flex flex-wrap gap-2">
            {canApprove && (
              <Button variant="primary" onClick={() => setStep("confirm-approve")}>
                Approve
              </Button>
            )}
            {canReject && (
              <Button variant="destructive" onClick={() => setStep("confirm-reject")}>
                Reject
              </Button>
            )}
            {!canApprove && !canReject && (
              <p className="text-sm text-muted">You do not have permission to act on this approval.</p>
            )}
          </div>
        )}
      </div>

      {step === "confirm-approve" && (
        <ConfirmApprovalDialog
          detail={detail}
          submitting={submitting}
          onConfirm={runApprove}
          onClose={() => setStep("idle")}
        />
      )}
      {step === "confirm-reject" && (
        <RejectDialog
          detail={detail}
          submitting={submitting}
          onConfirm={runReject}
          onClose={() => setStep("idle")}
        />
      )}
    </>
  );
}
